import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const structuredGenerationComponent: ComponentDef = {
  id: 'structured-generation',
  name: 'Structured Generation',
  description: 'Constrain decoder outputs to match a grammar, schema, or regex.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    {
      name: 'variant',
      label: 'View',
      type: 'select',
      default: 'principles',
      options: [
        { label: 'Principles', value: 'principles' },
        { label: 'FSM & Token Masking', value: 'fsm_masking' },
        { label: 'Implementation Comparison', value: 'impl_comparison' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    if (variant === 'fsm_masking') {
      // FSM & Token Masking: state transitions -> valid tokens -> bitmask -> logits
      g.linear('logits', 'x', [d_model, d_model]);
      g.scale('current_state', 'logits', 'Current FSM State');
      g.scale('transition_table', 'current_state', 'Transition Table');
      g.scale('valid_states', 'transition_table', 'Next Valid States');
      g.scale('vocab_filter', 'valid_states', 'Vocab Filter');
      g.mask('bitmask', 'vocab_filter');
      g.mul('masked_logits', 'logits', 'bitmask');
      g.softmax('softmax', 'masked_logits', -1);
      g.scale('sample', 'softmax', 'Sample');
      g.output('out', 'sample');
    } else if (variant === 'impl_comparison') {
      // Implementation Comparison: outlines, llama.cpp, guidance pipelines
      // Outlines: token boundaries approach
      g.linear('logits', 'x', [d_model, d_model]);

      g.scale('constraint_outlines', 'logits', 'Constraint (Outlines)');
      g.scale('compiler_outlines', 'constraint_outlines', 'Token Boundary Compiler');
      g.mask('mask_outlines', 'compiler_outlines');

      // llama.cpp: grammar approach
      g.scale('constraint_llama', 'logits', 'Constraint (llama.cpp)');
      g.scale('compiler_llama', 'constraint_llama', 'Grammar Compiler');
      g.mask('mask_llama', 'compiler_llama');

      // Guidance: regex approach
      g.scale('constraint_guidance', 'logits', 'Constraint (Guidance)');
      g.scale('compiler_guidance', 'constraint_guidance', 'Regex Compiler');
      g.mask('mask_guidance', 'compiler_guidance');

      // Combine: show they all produce the same result
      g.softmax('softmax', 'mask_outlines', -1);
      g.scale('sample', 'softmax', 'Sample');
      g.output('out', 'sample');
    } else {
      // Principles (default): unconstrained logits -> constraint -> mask -> sample
      g.linear('logits', 'x', [d_model, d_model]);
      g.scale('constraint', 'logits', 'Constraint (Schema/Regex/Grammar)');
      g.scale('valid_tokens', 'constraint', 'Valid Token Set');
      g.mask('mask', 'valid_tokens');
      g.scale('valid_only', 'mask', 'Sample from Valid Only');
      g.softmax('softmax', 'valid_only', -1);
      g.output('out', 'softmax');
    }

    return g.build();
  }
};
